import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getClientNotes, createClientNote, updateClientNote, deleteClientNote, toggleNoteImportance } from '../../services/notes';
import { createAuditLog } from '../../services/audit';
import './NotesTab.css';

export default function NotesTab({ clientId, client }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddNote, setShowAddNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteFormData, setNoteFormData] = useState({
    note: '',
    is_important: false,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadNotes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getClientNotes(clientId);
      setNotes(data);
    } catch (error) {
      console.error('Error loading notes:', error);
      setError('Failed to load notes: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handleAddNote = () => {
    setEditingNote(null);
    setNoteFormData({ note: '', is_important: false });
    setError('');
    setSuccess('');
    setShowAddNote(true);
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setNoteFormData({
      note: note.note,
      is_important: note.is_important,
    });
    setError('');
    setSuccess('');
    setShowAddNote(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingNote) {
        // Update existing note
        await updateClientNote(editingNote.id, {
          note: noteFormData.note,
          is_important: noteFormData.is_important,
        });
        await createAuditLog('update', 'client_note', editingNote.id, {
          client_id: clientId,
          client_name: client?.name,
          is_important: noteFormData.is_important,
        });
        setSuccess('Note updated successfully!');
      } else {
        // Create new note
        const newNote = await createClientNote({
          client_id: clientId,
          note: noteFormData.note,
          is_important: noteFormData.is_important,
          created_by: user?.id,
        });
        await createAuditLog('create', 'client_note', newNote.id, {
          client_id: clientId,
          client_name: client?.name,
          is_important: noteFormData.is_important,
        });
        setSuccess('Note added successfully!');
      }

      await loadNotes();
      setTimeout(() => {
        setShowAddNote(false);
        setSuccess('');
      }, 1000);
    } catch (error) {
      setError(error.message || 'Failed to save note');
    }
  };

  const handleDelete = async (noteId) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      try {
        await deleteClientNote(noteId);
        await createAuditLog('delete', 'client_note', noteId, {
          client_id: clientId,
          client_name: client?.name,
        });
        await loadNotes();
        setSuccess('Note deleted successfully!');
        setTimeout(() => setSuccess(''), 2000);
      } catch (error) {
        setError('Failed to delete note: ' + error.message);
      }
    }
  };

  const handleToggleImportance = async (note) => {
    try {
      await toggleNoteImportance(note.id, !note.is_important);
      await createAuditLog('update', 'client_note', note.id, {
        client_id: clientId,
        client_name: client?.name,
        is_important: !note.is_important,
        action: 'toggle_importance',
      });
      await loadNotes();
    } catch (error) {
      setError('Failed to update note: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="notes-tab">
        <div className="loading-state">Loading notes...</div>
      </div>
    );
  }

  return (
    <div className="notes-tab">
      <div className="notes-header">
        <h2>Client Notes</h2>
        <button className="btn-add-note" onClick={handleAddNote}>
          + Add Note
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {notes.length === 0 ? (
        <div className="empty-state">
          <p>No notes yet. Add your first note to get started.</p>
        </div>
      ) : (
        <div className="notes-list">
          {notes.map((note) => (
            <div key={note.id} className={`note-card ${note.is_important ? 'important' : ''}`}>
              <div className="note-header">
                <div className="note-meta">
                  <span className="note-date">
                    {new Date(note.created_at).toLocaleDateString()} {new Date(note.created_at).toLocaleTimeString()}
                  </span>
                  {note.is_important && (
                    <span className="important-badge">Important</span>
                  )}
                </div>
                <div className="note-actions">
                  <button
                    className={`btn-importance ${note.is_important ? 'active' : ''}`}
                    onClick={() => handleToggleImportance(note)}
                    title={note.is_important ? 'Mark as not important' : 'Mark as important'}
                  >
                    ⭐
                  </button>
                  <button
                    className="btn-edit"
                    onClick={() => handleEditNote(note)}
                    title="Edit note"
                  >
                    Edit
                  </button>
                  <button
                    className="btn-delete"
                    onClick={() => handleDelete(note.id)}
                    title="Delete note"
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div className="note-content">
                {note.note.split('\n').map((line, index) => (
                  <p key={index}>{line || '\u00A0'}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Note Modal */}
      {showAddNote && (
        <div className="modal-overlay" onClick={() => setShowAddNote(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingNote ? 'Edit Note' : 'Add New Note'}</h2>
              <button className="btn-close" onClick={() => setShowAddNote(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="note-form">
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}

              <div className="form-group">
                <label>Note *</label>
                <textarea
                  rows="8"
                  required
                  value={noteFormData.note}
                  onChange={(e) => setNoteFormData({ ...noteFormData, note: e.target.value })}
                  placeholder="Enter your note here..."
                  className="note-textarea"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label checkbox-row">
                  <input
                    type="checkbox"
                    checked={noteFormData.is_important}
                    onChange={(e) => setNoteFormData({ ...noteFormData, is_important: e.target.checked })}
                  />
                  <span>Mark as important (will show as popup when viewing client profile)</span>
                </label>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowAddNote(false);
                    setError('');
                    setSuccess('');
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingNote ? 'Update Note' : 'Add Note'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
